import apiSlice, { providesList } from "../../app/api/apiSlice";

export const cargoApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getCountryCodes: builder.query({
			query: () => `/api/geocode/getCountryCodes`,
			providesTags: ["CountryCodes"],
		}),
		getCargosForNotifications: builder.query({
			query: () => `/api/cargos/cargoOffers`,
			providesTags: (result) =>
				providesList(result?.offers || null, "CargosOffers"),
		}),
		getCurrentUserCargos: builder.query({
			query: ({ status, page, perPage, search }) =>
				`/api/cargos?status=${status}&page=${
					page + 1
				}&per_page=${perPage}&q=${search}`,
			providesTags: (result) => providesList(result?.data || null, "Cargos"),
		}),
		getCargoWithId: builder.query({
			query: (cargoId) => `/api/cargo/${cargoId}`,
			providesTags: (result, error, id) => [
				{ type: "Cargos", id: "LIST" },
				{ type: "Cargos", id },
			],
		}),
		getCurrentUserCompanyCargos: builder.query({
			query: ({ status, page, perPage, search, branch }) =>
				`/api/company/cargos?status=${status}&page=${
					page + 1
				}&per_page=${perPage}&q=${search}&branch=${branch}`,
			keepUnusedDataFor: 180,
			providesTags: (result) =>
				providesList(result?.data || null, "CompanyCargos"),
		}),
		addCargo: builder.mutation({
			query: (data) => ({
				url: `/api/cargo`,
				method: "POST",
				body: { data },
			}),
			invalidatesTags: [
				{ type: "Cargos", id: "LIST" },
				{ type: "CompanyCargos", id: "LIST" },
				{ type: "Stats", id: "LIST" },
				{ type: "CompanyStats", id: "GENERAL_LIST" },
				{ type: "CompanyStats", id: "IN_TIME_LIST" },
				{ type: "CompanyStats", id: "RELATION_LIST" },
				{ type: "CompanyStats", id: "ADMIN_LIST" },
				"LatestCargosParams",
			],
		}),
		closeCargo: builder.mutation({
			query: ({ cargoId, data }) => ({
				url: `/api/cargo/${cargoId}/close`,
				method: "PUT",
				body: { ...data },
			}),
			invalidatesTags: [
				{ type: "Cargos", id: "LIST" },
				{ type: "CompanyCargos", id: "LIST" },
				{ type: "Stats", id: "LIST" },
				{ type: "CompanyStats", id: "GENERAL_LIST" },
				{ type: "CompanyStats", id: "IN_TIME_LIST" },
				{ type: "CompanyStats", id: "RELATION_LIST" },
				{ type: "CompanyStats", id: "ADMIN_LIST" },
			],
		}),
		closeManyCargos: builder.mutation({
			query: ({ data }) => ({
				url: `/api/cargos/close`,
				method: "POST",
				body: { cargosToClose: data },
			}),
			invalidatesTags: [
				{ type: "Cargos", id: "LIST" },
				{ type: "CompanyCargos", id: "LIST" },
				{ type: "Stats", id: "LIST" },
				{ type: "CompanyStats", id: "GENERAL_LIST" },
				{ type: "CompanyStats", id: "IN_TIME_LIST" },
				{ type: "CompanyStats", id: "RELATION_LIST" },
				{ type: "CompanyStats", id: "ADMIN_LIST" },
			],
		}),
		editCargo: builder.mutation({
			query: ({ cargoId, data }) => ({
				url: `/api/cargo/${cargoId}`,
				method: "PUT",
				body: { ...data },
			}),
			invalidatesTags: [
				{ type: "Cargos", id: "LIST" },
				{ type: "CompanyCargos", id: "LIST" },
			],
		}),
		addNoteToCargo: builder.mutation({
			query: ({ cargoId, publicNote, privateNote }) => ({
				url: `/api/cargo/${cargoId}/note`,
				method: "POST",
				body: { publicNote, privateNote },
			}),
			invalidatesTags: [
				{ type: "Cargos", id: "LIST" },
				{ type: "CompanyCargos", id: "LIST" },
			],
		}),
		generatePostalCodes: builder.mutation({
			query: (data) => ({
				url: `/api/geocode/maskPostcodes`,
				method: "POST",
				body: { data },
			}),
		}),
		refreshCargo: builder.mutation({
			query: ({ cargoId }) => ({
				url: `/api/cargo/${cargoId}/refresh`,
				method: "POST",
				body: { cargoId },
			}),
			invalidatesTags: [{ type: "Cargos", id: "LIST" }],
		}),
		refreshManyCargos: builder.mutation({
			query: ({ scope }) => ({
				url: `/api/cargos/refresh`,
				method: "POST",
				body: { scope },
			}),
			invalidatesTags: [{ type: "Cargos", id: "LIST" }],
		}),
		sendRealizationRequest: builder.mutation({
			query: ({ data }) => ({
				url: `/api/cargos/request-realization-status`,
				method: "POST",
				body: { cargosToRequestRealizationStatus: data },
			}),
			invalidatesTags: [{ type: "Cargos", id: "LIST" }],
		}),
		// TEMPLATES & LATEST CARGO PARAMS
		getCargoTemplates: builder.query({
			query: () => `/api/cargos/templates`,
			providesTags: ["CargoTemplates"],
		}),
		addCargoTemplate: builder.mutation({
			query: ({ data }) => ({
				url: `/api/cargo/templates`,
				method: "POST",
				body: { ...data },
			}),
			invalidatesTags: ["CargoTemplates"],
		}),
		editCargoTemplate: builder.mutation({
			query: ({ templateId, name }) => ({
				url: `/api/cargo/templates/${templateId}`,
				method: "PUT",
				body: { name },
			}),
			invalidatesTags: ["CargoTemplates"],
		}),
		removeCargoTemplate: builder.mutation({
			query: ({ templateId }) => ({
				url: `/api/cargo/templates/${templateId}`,
				method: "DELETE",
			}),
			invalidatesTags: ["CargoTemplates"],
		}),
		getLatestCargosParams: builder.query({
			query: () => `/api/cargos/latest-params`,
			providesTags: ["LatestCargosParams"],
		}),
		// CUSTOM QUICK OPTIONS FOR CARGOS
		editCargoQuickOptions: builder.mutation({
			query: ({ type, data }) => ({
				url: `/api/cargos-quick-options`,
				method: "PUT",
				body: { type, data },
			}),
			invalidatesTags: [{ type: "Users", id: "LIST" }],
		}),
		// AI
		uploadCargoScreenShot: builder.mutation({
			query: ({ data }) => ({
				url: `/api/ai/newJob`,
				method: "POST",
				body: data,
			}),
			invalidatesTags: [{ type: "Users", id: "LIST" }],
		}),
		uploadCargoText: builder.mutation({
			query: ({ data }) => ({
				url: `/api/ai/newJob-text`,
				method: "POST",
				body: data,
			}),
			invalidatesTags: [{ type: "Users", id: "LIST" }],
		}),
		checkJobStatus: builder.query({
			query: ({ jobId }) => `/api/ai/getJobData/${jobId}`,
		}),
		saveAIRules: builder.mutation({
			query: ({ data }) => ({
				url: `/api/ai/rules`,
				method: "PUT",
				body: data,
			}),
			invalidatesTags: [{ type: "Users", id: "LIST" }],
		}),
		sendAIAccuracy: builder.mutation({
			query: ({ jobId, data }) => ({
				url: `/api/ai/sendAccuracy/${jobId}`,
				method: "PUT",
				body: data,
			}),
		}),
	}),
	overrideExisting: true,
});

export const {
	useGetCountryCodesQuery,
	useGetCurrentUserCargosQuery,
	useGetCargoWithIdQuery,
	useGetCurrentUserCompanyCargosQuery,
	useAddCargoMutation,
	useCloseCargoMutation,
	useCloseManyCargosMutation,
	useSendRealizationRequestMutation,
	useEditCargoMutation,
	useAddNoteToCargoMutation,
	useGeneratePostalCodesMutation,
	useRefreshCargoMutation,
	useRefreshManyCargosMutation,
	useGetCargosForNotificationsQuery,
	useGetCargoTemplatesQuery,
	useAddCargoTemplateMutation,
	useEditCargoTemplateMutation,
	useRemoveCargoTemplateMutation,
	useGetLatestCargosParamsQuery,
	useEditCargoQuickOptionsMutation,
	useUploadCargoScreenShotMutation,
	useUploadCargoTextMutation,
	useCheckJobStatusQuery,
	useSaveAIRulesMutation,
	useSendAIAccuracyMutation,
} = cargoApiSlice;
